import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SkyplusMenuAdminService, MenuAdminItem } from '../../shared/menu/menu-admin.service';

/**
 * Menu Manager (SKYPLUS_ADMIN) — low-code CRUD over the menu_item table so the entire
 * navigation can be managed from the UI. Editing the DB controls STRUCTURE + visibility;
 * the license_tier set here is still enforced against the signed license by the backend,
 * so enabling a menu does NOT bypass licensing.
 */
@Component({
  selector: 'skyplus-menu-manager',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
  <div class="mm-wrap">
    <div class="mm-head">
      <h1>Menu Manager</h1>
      <button class="mm-add" (click)="newItem()" [disabled]="!!editing">+ Add menu item</button>
    </div>
    <p class="mm-note">Controls the system navigation. The <b>license tier</b> is still enforced against the
       signed license — enabling a menu here will not show it unless the license allows the module.</p>

    <div *ngIf="loading" class="mm-muted">Loading…</div>
    <div *ngIf="error" class="mm-err">{{ error }}</div>

    <!-- Edit / create form -->
    <div *ngIf="editing as e" class="mm-form">
      <h2>{{ isNew ? 'New menu item' : 'Edit: ' + e.label }}</h2>
      <div class="mm-fields">
        <label>Key <input [(ngModel)]="e.menuKey" [disabled]="!isNew" placeholder="unique-key"></label>
        <label>Label <input [(ngModel)]="e.label" placeholder="Display name"></label>
        <label>Icon <input [(ngModel)]="e.icon" placeholder="material icon e.g. folder_open"></label>
        <label>Route <input [(ngModel)]="e.route" placeholder="/dashboard (blank for category)"></label>
        <label>Parent
          <select [(ngModel)]="e.parentId">
            <option [ngValue]="null">— none (top level) —</option>
            <option *ngFor="let p of parentOptions(e)" [ngValue]="p.id">{{ p.label }} ({{ p.menuType }})</option>
          </select>
        </label>
        <label>Order <input type="number" [(ngModel)]="e.sortOrder"></label>
        <label>Type
          <select [(ngModel)]="e.menuType">
            <option value="HEADER">HEADER</option>
            <option value="SIDEBAR_CATEGORY">SIDEBAR_CATEGORY</option>
            <option value="SIDEBAR_ITEM">SIDEBAR_ITEM</option>
          </select>
        </label>
        <label>License tier
          <select [(ngModel)]="e.licenseTier">
            <option value="CORE">CORE</option>
            <option value="STANDARD">STANDARD</option>
            <option value="PREMIUM">PREMIUM</option>
            <option value="CUSTOM">CUSTOM</option>
          </select>
        </label>
        <label>Required role <input [(ngModel)]="e.requiredRole" placeholder="blank = everyone"></label>
        <label>Workflow type <input [(ngModel)]="e.workflowType" placeholder="CONTRACT | MAIL | null"></label>
        <label>Color <input [(ngModel)]="e.color" placeholder="#244062"></label>
        <label class="mm-check"><input type="checkbox" [(ngModel)]="e.isActive"> Active</label>
      </div>
      <div class="mm-actions">
        <button class="mm-save" (click)="save()" [disabled]="saving || !e.menuKey?.trim() || !e.label?.trim()">{{ saving ? 'Saving…' : 'Save' }}</button>
        <button class="mm-cancel" (click)="cancel()" [disabled]="saving">Cancel</button>
        <span *ngIf="msg" class="mm-msg" [class.bad]="!ok">{{ msg }}</span>
      </div>
    </div>

    <!-- Filter + tree controls -->
    <div *ngIf="!loading" class="mm-toolbar">
      <input class="mm-filter" [(ngModel)]="filter" placeholder="Filter by label, key or route…">
      <button (click)="expandAll()">Expand all</button>
      <button (click)="collapseAll()">Collapse all</button>
      <span class="mm-muted" *ngIf="filter">filtered (flat view)</span>
    </div>

    <!-- Table -->
    <table *ngIf="!loading" class="mm-table">
      <thead>
        <tr><th>Menu item (tree)</th><th>Key</th><th>Route</th><th>Type</th><th>Tier</th><th>Role</th><th>Order</th><th>Active</th><th></th></tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of displayRows()" [class.parent-row]="row.depth === 0">
          <td [style.paddingLeft.px]="10 + row.depth * 22">
            <span *ngIf="row.hasChildren" class="tree-toggle" (click)="toggle(row.item.id)">{{ row.collapsed ? '▸' : '▾' }}</span>
            <span *ngIf="!row.hasChildren && row.depth > 0" class="tree-branch">└&nbsp;</span>
            <span class="material-icons tree-ic" *ngIf="row.item.icon">{{ row.item.icon }}</span>
            {{ row.item.label }}
            <span *ngIf="row.hasChildren" class="tree-count">({{ childCount(row.item.id) }})</span>
          </td>
          <td class="mono">{{ row.item.menuKey }}</td>
          <td class="mono">{{ row.item.route || '—' }}</td>
          <td>{{ row.item.menuType }}</td>
          <td>{{ row.item.licenseTier }}</td>
          <td>{{ row.item.requiredRole || '—' }}</td>
          <td>{{ row.item.sortOrder }}</td>
          <td><span class="dot" [class.on]="row.item.isActive"></span></td>
          <td class="mm-rowactions">
            <button (click)="edit(row.item)">Edit</button>
            <button class="del" (click)="remove(row.item)">Delete</button>
          </td>
        </tr>
        <tr *ngIf="!items.length"><td colspan="9" class="mm-muted">No menu items.</td></tr>
      </tbody>
    </table>
  </div>
  `,
  styles: [`
    .mm-wrap { padding: 24px; }
    .mm-head { display: flex; align-items: center; justify-content: space-between; }
    .mm-head h1 { font-size: 22px; font-weight: 600; margin: 0; }
    .mm-note { color: #6b7280; font-size: 13px; max-width: 760px; }
    .mm-add, .mm-save { background: #244062; color: #fff; border: 0; border-radius: 6px; padding: 8px 14px; font-weight: 600; cursor: pointer; }
    .mm-cancel { background: #eef2f8; color: #244062; border: 0; border-radius: 6px; padding: 8px 14px; cursor: pointer; margin-left: 8px; }
    .mm-add:disabled, .mm-save:disabled { opacity: .5; cursor: default; }
    .mm-muted { color: #6b7280; } .mm-err, .mm-msg.bad { color: #c0392b; }
    .mm-form { border: 1px solid #dbe2ec; border-radius: 8px; padding: 16px; margin: 16px 0; background: #fafbfd; }
    .mm-form h2 { font-size: 16px; margin: 0 0 12px; }
    .mm-fields { display: grid; grid-template-columns: repeat(2, minmax(220px, 1fr)); gap: 10px 18px; }
    .mm-fields label { display: flex; flex-direction: column; font-size: 12px; color: #4b5563; gap: 4px; }
    .mm-fields input, .mm-fields select { padding: 7px 9px; border: 1px solid #cdd5e0; border-radius: 6px; font-size: 13px; }
    .mm-fields .mm-check { flex-direction: row; align-items: center; gap: 8px; }
    .mm-actions { margin-top: 14px; display: flex; align-items: center; gap: 4px; }
    .mm-msg { margin-left: 12px; font-size: 13px; color: #1e7e34; }
    .mm-table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 13px; }
    .mm-table th, .mm-table td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #eef1f5; }
    .mm-table th { color: #6b7280; font-weight: 600; }
    .mono { font-family: monospace; font-size: 12px; }
    .dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: #d0d5dd; }
    .dot.on { background: #2ecc71; }
    .mm-rowactions button { border: 0; background: #eef2f8; color: #244062; border-radius: 5px; padding: 4px 10px; cursor: pointer; margin-right: 6px; }
    .mm-rowactions .del { background: #fdecea; color: #c0392b; }
    .parent-row td { font-weight: 600; background: #f7f9fc; }
    .tree-branch { color: #9aa6b2; }
    .tree-ic { font-size: 16px; vertical-align: middle; color: #5a6b7d; margin-right: 4px; }
    .tree-toggle { cursor: pointer; display: inline-block; width: 16px; color: #5a6b7d; user-select: none; }
    .tree-count { color: #9aa6b2; font-size: 12px; margin-left: 6px; font-weight: 400; }
    .mm-toolbar { display: flex; align-items: center; gap: 8px; margin: 12px 0 6px; }
    .mm-filter { flex: 0 0 320px; padding: 7px 10px; border: 1px solid #cdd5e0; border-radius: 6px; font-size: 13px; }
    .mm-toolbar button { border: 0; background: #eef2f8; color: #244062; border-radius: 6px; padding: 6px 12px; cursor: pointer; }
  `]
})
export class MenuManagerComponent implements OnInit {
  private svc = inject(SkyplusMenuAdminService);

  items: MenuAdminItem[] = [];
  loading = true;
  error = '';
  collapsed = new Set<number>();
  filter = '';

  editing: MenuAdminItem | null = null;
  isNew = false;
  saving = false;
  msg = '';
  ok = false;

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.error = '';
    this.svc.list().subscribe({
      next: (rows) => { this.items = rows; this.loading = false; },
      error: () => { this.error = 'Could not load menu items.'; this.loading = false; }
    });
  }

  newItem(): void {
    this.editing = {
      menuKey: '', label: '', icon: 'circle', route: '', parentId: null, sortOrder: 10,
      color: null, requiredRole: null, licenseTier: 'CORE', isActive: true,
      menuType: 'HEADER', workflowType: null
    };
    this.isNew = true;
    this.msg = '';
  }

  edit(it: MenuAdminItem): void {
    this.editing = { ...it };
    this.isNew = false;
    this.msg = '';
  }

  cancel(): void { this.editing = null; this.msg = ''; }

  save(): void {
    const e = this.editing;
    if (!e) { return; }
    this.saving = true;
    this.msg = '';
    const op = this.isNew ? this.svc.create(e) : this.svc.update(e.id as number, e);
    op.subscribe({
      next: () => { this.saving = false; this.ok = true; this.editing = null; this.load(); },
      error: (err) => { this.saving = false; this.ok = false; this.msg = err?.error?.message || 'Save failed.'; }
    });
  }

  remove(it: MenuAdminItem): void {
    if (!confirm(`Delete "${it.label}"? Any child items are removed too.`)) { return; }
    this.svc.remove(it.id as number).subscribe({
      next: () => this.load(),
      error: (e) => { this.error = e?.error?.message || 'Delete failed.'; }
    });
  }

  /** Valid parents = HEADER or SIDEBAR_CATEGORY items (excluding self). */
  parentOptions(current: MenuAdminItem): MenuAdminItem[] {
    return this.items.filter((i) =>
      i.id !== current.id && (i.menuType === 'HEADER' || i.menuType === 'SIDEBAR_CATEGORY'));
  }

  toggle(id: number | null | undefined): void {
    if (id == null) { return; }
    if (this.collapsed.has(id)) { this.collapsed.delete(id); } else { this.collapsed.add(id); }
  }

  collapseAll(): void {
    this.collapsed = new Set(
      this.items.filter(p => this.items.some(c => c.parentId === p.id)).map(p => p.id as number));
  }

  expandAll(): void { this.collapsed = new Set(); }

  childCount(id: number | null | undefined): number {
    return id == null ? 0 : this.items.filter(i => i.parentId === id).length;
  }

  /**
   * Rows for the table. With a filter, returns a flat list of matches; otherwise an
   * indented parent→child tree (skipping children of collapsed parents).
   */
  displayRows(): { item: MenuAdminItem; depth: number; hasChildren: boolean; collapsed: boolean }[] {
    const f = this.filter.trim().toLowerCase();
    if (f) {
      return this.items
        .filter(i => `${i.label} ${i.menuKey} ${i.route ?? ''}`.toLowerCase().includes(f))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map(item => ({ item, depth: 0, hasChildren: false, collapsed: false }));
    }
    const byParent = new Map<number | null, MenuAdminItem[]>();
    for (const it of this.items) {
      const key = (it.parentId ?? null) as number | null;
      const arr = byParent.get(key) ?? [];
      arr.push(it);
      byParent.set(key, arr);
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    const ids = new Set(this.items.map(i => i.id));
    const out: { item: MenuAdminItem; depth: number; hasChildren: boolean; collapsed: boolean }[] = [];
    const walk = (parentId: number | null, depth: number) => {
      for (const it of (byParent.get(parentId) ?? [])) {
        const kids = byParent.get((it.id ?? -1) as number) ?? [];
        const isCollapsed = it.id != null && this.collapsed.has(it.id);
        out.push({ item: it, depth, hasChildren: kids.length > 0, collapsed: isCollapsed });
        if (!isCollapsed) { walk((it.id ?? -1) as number, depth + 1); }
      }
    };
    walk(null, 0);
    // Orphans (parent not in the list) shown at top level so nothing is hidden.
    for (const it of this.items) {
      if (it.parentId != null && !ids.has(it.parentId) && !out.some(r => r.item === it)) {
        out.push({ item: it, depth: 0, hasChildren: false, collapsed: false });
      }
    }
    return out;
  }
}
